import { useState } from "react"
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
} from "@mui/material"
import {
  Visibility as VisibilityIcon,
  Comment as CommentIcon,
} from "@mui/icons-material"
import { useApiQuery, useApiMutation } from "../../hooks/useApi"
import { useAuth } from "../../contexts/AuthContext"
import { useQueryClient } from "@tanstack/react-query"
import api from "../../utils/api"
import JobsTable from "../../components/jobs/JobsTable"
import JobDetailsDialog from "../../components/jobs/JobDetailsDialog"

function FeesPendingJobsPage() {
  const [selectedJob, setSelectedJob] = useState(null)
  const [jobDetailsForView, setJobDetailsForView] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [addCommentDialogOpen, setAddCommentDialogOpen] = useState(false)
  const [comment, setComment] = useState("")

  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: feesPendingJobs, isLoading } = useApiQuery(
    ["feesPendingJob", user?._id],
    `jobs/fees-pending`
  )

  const setJobCommentMutation = useApiMutation(
    `/jobs/${selectedJob?.JobId}/comments`,
    "POST",
    {
      onSuccess: () => {
        setAddCommentDialogOpen(false)
        setComment("")
        queryClient.invalidateQueries({ queryKey: ["feesPendingJob"] })
      },
    }
  )

  // Define table columns with custom render for completed date
  const columns = [
    { id: "jobId", label: "Job ID", field: "JobId", type: "jobId" },
    {
      id: "client",
      label: "Client",
      render: (job) => (
        <Box>
          <Typography variant="subtitle2">{job.clientName}</Typography>
          {job.clientPhoneNumber && (
            <Typography variant="caption" color="textSecondary">
              {job.clientPhoneNumber}
            </Typography>
          )}
        </Box>
      ),
    },
    { id: "jobType", label: "Job Type", field: "jobType" },
    { id: "dueDate", label: "Due Date", field: "dueDate", type: "date" },
    {
      id: "completedOn",
      label: "Completed On",
      render: (job) => {
        const date = job.completedAt || job.updatedAt
        return date ? new Date(date).toLocaleDateString() : "-"
      },
    },
    {
      id: "status",
      label: "Status",
      render: (job) => (
        <Chip label="Completed" color="success" size="small" />
      ),
    },
    {
      id: "fees",
      label: "Fees",
      render: (job) => (
        <Chip label={`${job?.fees}`} color="default" size="small" />
      ),
    },
    {
      id: "assignedTo",
      label: "Assigned To",
      render: (job) => (
        <Box>
          <Typography variant="body2">
            {job.assignedToName || "Unassigned"}
          </Typography>
          {job.secondaryUser && job.secondaryUser.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              + {job.secondaryUser.map((u) => u.name).join(", ")}
            </Typography>
          )}
        </Box>
      ),
    },
  ]

  // Define actions
  const actions = [
    {
      id: "view",
      label: "View Details",
      icon: <VisibilityIcon />,
    },
    {
      id: "addComment",
      label: "Add Comment",
      icon: <CommentIcon />,
    },
  ]

  const handleActionClick = async (action, job) => {
    setSelectedJob(job)

    switch (action.id) {
      case "view":
        // Fetch full job details
        try {
          const jobDetails = await api.get(`/jobs/${job.JobId}`)
          setJobDetailsForView(jobDetails.data[0])
          setViewDialogOpen(true)
        } catch (error) {
          console.error("Failed to fetch job details:", error)
        }
        break
      case "addComment":
        setAddCommentDialogOpen(true)
        break
      default:
        break
    }
  }

  const handleAddCommentSave = async () => {
    try {
      if (!comment) return
      const data = { commentText: comment }
      await setJobCommentMutation.mutateAsync(data)
    } catch (error) {
      console.error("Failed to add comment", error)
    }
  }

  if (isLoading) {
    return <Typography>Loading pending fees jobs...</Typography>
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Pending Fees Jobs
          </Typography>
        </Box>
      </Box>

      {/* Jobs Table - Reusable Component */}
      <JobsTable
        jobs={feesPendingJobs || []}
        columns={columns}
        actions={actions}
        onActionClick={handleActionClick}
        emptyMessage="No completed jobs found."
      />

      {/* View Details Dialog - Reusable Component */}
      <JobDetailsDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setJobDetailsForView(null)
          setSelectedJob(null)
        }}
        job={jobDetailsForView}
      />

      {/* Add Comment Dialog */}
      <Dialog
        open={addCommentDialogOpen}
        onClose={() => setAddCommentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.JobId} - {selectedJob.jobType}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Client: {selectedJob.clientName}
              </Typography>
              <TextField
                fullWidth
                label="Comments"
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add notes about the completed job..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCommentDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddCommentSave}
            variant="contained"
            disabled={!comment || setJobCommentMutation.isPending}
          >
            {setJobCommentMutation.isPending ? "Adding..." : "Add Comment"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default FeesPendingJobsPage